import { useSpring } from "react-spring";
import { screens } from "../../constants/constants";
import { springConfigs } from "../../constants/springConfigs";
import { Hamburger } from "../shared/Hamburger";
import { RectangleButton } from "../shared/RectangleButton";
import { ScreenBackground } from "../shared/ScreenBackground";
import { Video } from "../shared/Video";
import { OnCurtainStart } from "../../types/types";
import { Arrows, LeftArrow, RightArrow } from "./components/Arrows";
import { Contour } from "./components/Contour";
import { BioComputer, CreatingA } from "./components/CreatingABioComputer";
import { HackingTheCell } from "./components/HackingTheCell";
import { IocoText } from "./components/IocoText";
import { Lines } from "./components/Lines";
import { PageNumber } from "./components/PageNumber";
import { useWindowSize } from "../../hooks/useWindowSize";
import { useMargins } from "../../hooks/hooks";

const ScreenLanding = ({
  isCurtainActive,
  isCurtainIntro,
  onCurtainStart,
}: {
  isCurtainActive: boolean;
  isCurtainIntro: boolean;
  onCurtainStart: OnCurtainStart;
}) => {
  const mesh = [
    "radial-gradient(circle at 50% 0%, #8C4AC2 0, transparent 95%)",
    "linear-gradient(#4500C6 -30%, #331B54)",
  ];

  const background = `
        ${mesh[0]},
        ${mesh[1]}
    `;

  const { liftAsset } = useSpring({
    from: { liftAsset: "10%" },
    to: { liftAsset: "0%" },
    config: {
      tension: 180,
      friction: 120,
      mass: 10,
      precision: 0.00001,
    },
  });

  const { opacity } = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: springConfigs.assetAppears,
  });

  /* 
    !isCurtainActive reveal content after curtain animated out
    (isCurtainActive && isCurtainIntro) when moving to next screen still show content
  */
  const isContentShown =
    !isCurtainActive || (isCurtainActive && isCurtainIntro);

  const { horizontal, vertical, h, w } = useWindowSize();
  const { screenContentVerticalPadding, screenContentHorizontalPadding } =
    useMargins();

  return (
    <>
      <ScreenBackground
        {...{
          background,
          screenName: screens.landing,
          gridArea: "AppContentContainer",
        }}
      />
      <Contour {...{ gridArea: "AppContentContainer" }} />

      <Lines
        {...{
          gridArea: "AppContentContainer",
          numberOfLines: horizontal ? 8 : 5,
        }}
      />

      {true && isContentShown && (
        <Video
          {...{
            containerStyle: {
              width: horizontal ? w(70) : "150%",
              gridArea: "AppContentContainer",
              placeSelf: horizontal ? "start end" : "center start",
            },
            videoStyle: {
              width: horizontal ? "100%" : "150%",
              marginRight: horizontal ? "5%" : "",
              marginTop: liftAsset,
              opacity,
            },
          }}
        />
      )}

      {isContentShown && (
        <div
          id="contentGrid"
          className=""
          {...{
            style: {
              gridArea: "AppContentContainer",
              display: "grid",
              padding: `${screenContentVerticalPadding} ${screenContentHorizontalPadding}`, // start with first line
              gridTemplateColumns: `repeat(${
                horizontal ? 7 : 4
              }, minmax(0, 1fr))`, // https://stackoverflow.com/questions/43311943/prevent-content-from-expanding-grid-items
              gridTemplateRows: "repeat(11, minmax(0, 1fr))", // https://stackoverflow.com/questions/43311943/prevent-content-from-expanding-grid-items
            },
          }}
        >
          <IocoText
            {...{
              onCurtainStart,
              gridArea: horizontal ? "1/1" : "1/1", // second row first colum
            }}
          />
          <Hamburger
            {...{
              onCurtainStart,
              gridArea: horizontal ? "1/7/1/8" : "1/-3/1/-1",
            }}
          />
          {horizontal && (
            <Arrows
              {...{
                gridArea: "5/1/7/2",
                onCurtainStart,
                screenLeft: "preorder",
                screenRight: "product",
              }}
            />
          )}
          {vertical && (
            <div
              id=""
              {...{
                style: {
                  gridArea: "-3/1/-1/2",
                  display: "grid",
                  // placeSelf: "center",
                  width: "66.6%",
                  gap: h(1),
                },
              }}
            >
              <LeftArrow
                {...{
                  onCurtainStart,
                  screenLeft: "preorder",
                  screenRight: "product",
                  containerStyle: {
                    width: "87.5%", // matain a 7/8th ratio
                    placeSelf: "start",
                  },
                }}
              />
              <RightArrow
                {...{
                  onCurtainStart,
                  screenLeft: "preorder",
                  screenRight: "product",
                }}
              />
            </div>
          )}
          <HackingTheCell {...{ gridArea: "4/1/5/6" }} />
          <div
            id="CreatingABioComputer"
            className=""
            {...{
              style: {
                gridArea: horizontal ? "5/2/9/-1" : "5/1/6/-1",
              },
            }}
          >
            <CreatingA />
            <BioComputer />
          </div>

          <RectangleButton
            {...{
              gridArea: horizontal ? "9/2/9/4" : "-2/2/-1/-1",
              text: "LEARN MORE",
              width: horizontal ? "75%" : "100%",
              screen: "intro",
              curtainTo: screens.menu,
              onCurtainStart,
            }}
          />
          <PageNumber {...{ gridArea: horizontal ? "5/7/7/8" : "2/1/3/4" }} />
        </div>
      )}
    </>
  );
};

export { ScreenLanding };
